import { useEffect } from "react";
import {
    POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID,
    POST_TO_RP_WEBHOOK_PROPERTY_TYPE_ID,
    RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID,
    RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID,
    RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID,
    RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID,
    RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID,
    RP_WEBHOOK_URL_PROPERTY_TYPE_ID,
    RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID
} from "@constants/webhook-constants";
import { PageLevelTypes } from "@enums/page-level-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { WebhookConfigurationContainer } from "@containers/common/properties-sections/webhook-configuration-container/webhook-configuration-container";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { getPropertyValuesPassedToService } from "@utils/applied-profile-util";
import { SelectOption } from "@idw-types/common";
import { SecureWebhookTypes } from "@enums/secure-webhook-types";

type WorkflowWebhookConfigurationContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    apiKey: string;
    documentationLinks: Array<AxnDocumentationLink>;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    updateProperties: (properties: Array<{
        propertyTypeId: string;
        newValue: string;
        added: boolean;
    }>) => void;
};

export const WorkflowWebhookConfigurationContainer = (props: WorkflowWebhookConfigurationContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        apiKey,
        documentationLinks,
        workflowDetailsConfigurationState,
        updateProperties
    } = props;

    useEffect(() => {
        const postToRpWebhookPropertyValue = getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, POST_TO_RP_WEBHOOK_PROPERTY_TYPE_ID);
        if (!isDisabled && isStringTrue(postToRpWebhookPropertyValue)) {
            const propertiesToUpdate: Array<{ propertyTypeId: string; newValue: string; added: boolean; }> = [];
            if (getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID) === null) {
                propertiesToUpdate.push({ propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "false", added: true });
            }
            if (getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID) === null) {
                propertiesToUpdate.push({ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: true });
            }
            if (getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID) === null) {
                propertiesToUpdate.push({ propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: "false", added: true });
            }
            if (getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID) === null) {
                propertiesToUpdate.push({ propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: null, added: true });
            }
            updateProperties(propertiesToUpdate);
        }
    }, []);

    const onChangeEnableWebhook = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([
                { propertyTypeId: POST_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID, newValue: apiKey, added: true },
                { propertyTypeId: RP_WEBHOOK_URL_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: true },
                { propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: null, added: true },
            ]);
        } else {
            updateProperties([
                { propertyTypeId: POST_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_URL_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
    };

    const onChangeIncludeUserData = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([{ propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "true", added: true }]);
        } else {
            updateProperties([{ propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: "false", added: true }]);
        }
    };

    const onChangeAddWebhookWrapperObject = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([{ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: "", added: true }]);
        } else {
            updateProperties([{ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: true }]);
        }
    };

    const onChangeSetToFlatEndpoint = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([{ propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: "true", added: true }]);
        } else {
            updateProperties([{ propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: "false", added: true }]);
        }
    };

    const onChangeSecurityTypeWebhookEnabled = (newValue: SelectOption) => {
        if (newValue.value === SecureWebhookTypes.NONE) {
            updateProperties([
                { propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
        if (newValue.value === SecureWebhookTypes.BASIC) {
            updateProperties([
                { propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: newValue.value, added: true },
                { propertyTypeId: RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
        else if (newValue.value === SecureWebhookTypes.OAUTH) {
            updateProperties([
                { propertyTypeId: RP_WEBHOOK_AUTH_TYPE_PROPERTY_TYPE_ID, newValue: newValue.value, added: true },
                { propertyTypeId: RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
    };
    return (
        <WebhookConfigurationContainer
            isDisabled={isDisabled}
            validationErrors={validationErrors}
            documentationLinks={documentationLinks}
            appliedProfiles={workflowDetailsConfigurationState.appliedProfiles}
            properties={[]}
            pageLevel={PageLevelTypes.WORKFLOW}
            workflowId={workflowDetailsConfigurationState.id}
            canModifyNonRestrictedProperties={workflowDetailsConfigurationState.permissions.canModifyNonRestrictedProperties}
            updateProperties={updateProperties}
            onChangeAddWebhookWrapperObject={onChangeAddWebhookWrapperObject}
            onChangeEnableWebhook={onChangeEnableWebhook}
            onChangeIncludeUserData={onChangeIncludeUserData}
            onChangeSecurityTypeWebhookEnabled={onChangeSecurityTypeWebhookEnabled}
            onChangeSetToFlatEndpoint={onChangeSetToFlatEndpoint}
        />
    );
};